import { z } from 'zod';
import { ExaminedResultEnum, ExaminedResultEnumZod } from './Enums';
import {
  CheckNumber,
  CheckStudentBaseData,
  checkExaminedResultEnum,
  CheckNumberRange,
  CheckBloodNumberRange,
} from '../utils/checkFormat';
import { PhysicalSetting} from '../model';
import {
  CheckShouldUseIdentify,
} from '../utils';
import apis from '../apis';
type physicalSettingObject = {
  [key: number]: PhysicalSetting;
};

export const PhiBloodZod = z.object({
  sem: z.number(),
  year: z.number(),
  hb: z.number().nullish(),
  wbc: z.number().nullish(),
  rbc: z.number().nullish(),
  pl: z.number().nullish(),
  mcv: z.number().nullish(),
  ht: z.number().nullish(),
  cl: z.number().nullish(),
  cr: z.number().nullish(),
  ua: z.number().nullish(),
  bun: z.number().nullish(),
  got: z.number().nullish(),
  gpt: z.number().nullish(),
  hbsa: z.number().nullish(),
  antiHB: z.number().nullish(),
  hbResult: ExaminedResultEnumZod,
  wbcResult: ExaminedResultEnumZod,
  rbcResult: ExaminedResultEnumZod,
  plResult: ExaminedResultEnumZod,
  mcvResult: ExaminedResultEnumZod,
  htResult: ExaminedResultEnumZod,
  clResult: ExaminedResultEnumZod,
  crResult: ExaminedResultEnumZod,
  uaResult: ExaminedResultEnumZod,
  bunResult: ExaminedResultEnumZod,
  gotResult: ExaminedResultEnumZod,
  gptResult: ExaminedResultEnumZod,
  hbsaResult: ExaminedResultEnumZod,
  antiHBResult: ExaminedResultEnumZod,
  bloodCheckMemo: z.string().nullish(),
  tgResult: ExaminedResultEnumZod,
  tg: z.number().nullish(),
  hdlResult: ExaminedResultEnumZod,
  hdl: z.number().nullish(),
  ldlResult: ExaminedResultEnumZod,
  ldl: z.number().nullish(),
  acResult: ExaminedResultEnumZod,
  ac: z.number().nullish(),
  pcResult: ExaminedResultEnumZod,
  pc: z.number().nullish(),
  hBeAgResult: ExaminedResultEnumZod,
  hBeAg: z.number().nullish(),
});

export const PhiBloodFileZod = z
  .object({
    身分證: z.string().nullish(),
    學號: z.string().nullish(),
    年級班級座號: z.string().nullish(),
    血色素: z.string().nullish(),
    血色素判讀: z.string().nullish(),
    白血球: z.string().nullish(),
    白血球判讀: z.string().nullish(),
    紅血球: z.string().nullish(),
    紅血球判讀: z.string().nullish(),
    血小板: z.string().nullish(),
    血小板判讀: z.string().nullish(),
    平均血球容積: z.string().nullish(),
    平均血球容積判讀: z.string().nullish(),
    總膽固醇: z.string().nullish(),
    總膽固醇判讀: z.string().nullish(),
    肌酸酐: z.string().nullish(),
    肌酸酐判讀: z.string().nullish(),
    尿酸: z.string().nullish(),
    尿酸判讀: z.string().nullish(),
    SGOT: z.string().nullish(),
    SGOT判讀: z.string().nullish(),
    SGPT: z.string().nullish(),
    SGPT判讀: z.string().nullish(),
    B型肝炎表面抗原: z.string().nullish(),
    B型肝炎表面抗原判讀: z.string().nullish(),
    B型肝炎表面抗體: z.string().nullish(),
    B型肝炎表面抗體判讀: z.string().nullish(),
    三酸甘油酯: z.string().nullish(),
    三酸甘油酯判讀: z.string().nullish(),
    高密度膽固醇: z.string().nullish(),
    高密度膽固醇判讀: z.string().nullish(),
    低密度膽固醇: z.string().nullish(),
    低密度膽固醇判讀: z.string().nullish(),
    空腹血糖: z.string().nullish(),
    空腹血糖判讀: z.string().nullish(),
    飯後血糖: z.string().nullish(),
    飯後血糖判讀: z.string().nullish(),
    B型肝炎e抗原: z.string().nullish(),
    B型肝炎e抗原判讀: z.string().nullish(),
    血尿素氮: z.string().nullish(),
    血尿素氮判讀: z.string().nullish(),
    血球容積比: z.string().nullish(),
    血球容積比判讀: z.string().nullish(),
    備註: z.string().nullish(),
  })
  .passthrough()
  .transform(async(initialArg) => {
    const arg = normalizeKeys(initialArg);
    let _errorMsg = '';
    const errorSettingMsg:string[] = [];
    let _okUpload = true;
    let bloodSettingsData:string[] = [];
    let studentGrade = 0;
    let studentNo = 0;

    //檢查【身分證】、【學號】、【年級班級座號】格式
    const checkStudentBaseDataResult = CheckStudentBaseData(
      arg.身分證,
      arg.學號,
      arg.年級班級座號
    );
    const _pid = checkStudentBaseDataResult.pid;
    const _sid = checkStudentBaseDataResult.sid;
    const _grade = checkStudentBaseDataResult.grade;
    const _no = checkStudentBaseDataResult.no;
    const _seat = checkStudentBaseDataResult.seat;
    if (!checkStudentBaseDataResult.okUpload) {
      _okUpload = checkStudentBaseDataResult.okUpload;
      _errorMsg += checkStudentBaseDataResult.errorMsg;
    }

    if(checkStudentBaseDataResult.okUpload) {
      // 取得學生年級
      await getStudentGrade();
      // 取得追加設定
      await apis.getPhysicalSettings().then((s) => {
        bloodSettingsData = s.find(b => b.grade == studentGrade && b.no == studentNo)?.bloodAdditional || [];
      });
    }
    
    async function getStudentGrade() {
      const student = [{
        pid: _pid,
        sid: _sid,
        grade: _grade,
        no: _no,
        seat: _seat,
      }];
      const [cf, matchFunc] = CheckShouldUseIdentify(student);
      await apis.checkClassesStudentExistsWithGrade(
        student,
        cf
      )
      .then((result) => {
          if(result.grade && result.no) {
            studentGrade = result.grade;
            studentNo = result.no;
          } else {
            _errorMsg += '該學生不存在';
          }
      })  
      .catch((e) => {
        console.log('error',e)
      });
    }

    //血色素判讀
    const hbResultCheck = checkExaminedResultEnum(
      arg.血色素判讀,
      '血色素判讀'
    );
    if(hbResultCheck.isNull) {
      hbResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += hbResultCheck.errorMsg;
      _okUpload = _okUpload && hbResultCheck.okUpload;
    }
    const _hbResult = hbResultCheck.code;
    const hbInput = arg.血色素;
    const hbValue = parseRangeInput(hbInput);
    const { code: hb, ...hbCheck } = CheckBloodNumberRange(hbValue, 0, { min: 4, max: 20 }, '血色素');
    if(Number.isNaN(hb)) {
      _errorMsg+= '【血色素】格式有誤，';
    } else if(_hbResult != ExaminedResultEnum.NoExamined && !hb) {
      _errorMsg += hbCheck.errorMsg;
      _okUpload = _okUpload && hbCheck.okUpload;
    }
    const wbcResultCheck = checkExaminedResultEnum(
      arg.白血球判讀,
      '白血球判讀'
    );
    if(wbcResultCheck.isNull) {
      wbcResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += wbcResultCheck.errorMsg;
      _okUpload = _okUpload && wbcResultCheck.okUpload;
    }
    const _wbcResult = wbcResultCheck.code;
    const wbInput = arg.白血球;
    const wbValue = parseRangeInput(wbInput);
    const { code: wbc, ...wbcCheck } = CheckBloodNumberRange(
      wbValue || (arg as any)['白血球 '],
      0,
      { min: 0, max: 400 },
      '白血球'
    );
    if(Number.isNaN(wbc)) {
      _errorMsg+= '【白血球】格式有誤，';
    } else if(_wbcResult != ExaminedResultEnum.NoExamined && !wbc) {
      _errorMsg += wbcCheck.errorMsg;
      _okUpload = _okUpload && wbcCheck.okUpload;
    }
    const rbcResultCheck = checkExaminedResultEnum(
      arg.紅血球判讀,
      '紅血球判讀'
    );
    if(rbcResultCheck.isNull) {
      rbcResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += rbcResultCheck.errorMsg;
      _okUpload = _okUpload && rbcResultCheck.okUpload;
    }
    const _rbcResult = rbcResultCheck.code;
    const rbcInput = arg.紅血球;
    const rbcValue = parseRangeInput(rbcInput);
    const { code: rbc, ...rbcCheck } = CheckBloodNumberRange(
      rbcValue || (arg as any)['紅血球 '],
      0,
      { min: 0, max: 60 },
      '紅血球'
    );
    if(Number.isNaN(rbc)) {
      _errorMsg+= '【紅血球】格式有誤，';
    } else if(_rbcResult != ExaminedResultEnum.NoExamined && !rbc) {
      _errorMsg += rbcCheck.errorMsg;
      _okUpload = _okUpload && rbcCheck.okUpload;
    }
    const plResultCheck = checkExaminedResultEnum(
      arg.血小板判讀,
      '血小板判讀'
    );
    if(plResultCheck.isNull) {
      plResultCheck.code = ExaminedResultEnum.NoProblem;
    } else{
      _errorMsg += plResultCheck.errorMsg;
      _okUpload = _okUpload && plResultCheck.okUpload;
    }
    const _plResult = plResultCheck.code;
    const plInput = arg.血小板;
    const plValue = parseRangeInput(plInput);
    const { code: pl, ...plCheck } = CheckBloodNumberRange(plValue, 0, {min: 0, max: 1000}, '血小板');
    if(Number.isNaN(pl)) {
      _errorMsg+= '【血小板】格式有誤，';
    } else if(_plResult != ExaminedResultEnum.NoExamined && !pl) {
      _errorMsg += plCheck.errorMsg;
      _okUpload = _okUpload && plCheck.okUpload;
    }
    const mcvCheckResult = checkExaminedResultEnum(
      arg.平均血球容積判讀,
      '平均血球容積判讀'
    );
    if(mcvCheckResult.isNull) {
      mcvCheckResult.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += mcvCheckResult.errorMsg;
      _okUpload = _okUpload && mcvCheckResult.okUpload;
    }
    const _mcvResult = mcvCheckResult.code;
    const mcvInput = arg.平均血球容積;
    const mcvValue = parseRangeInput(mcvInput);
    const { code: mcv, ...mcvCheck } = CheckBloodNumberRange(
      mcvValue,
      0,
      { min: 50, max: 200 },
      '平均血球容積'
    );
    if(Number.isNaN(mcv)) {
      _errorMsg+= '【平均血球容積】格式有誤，';
    } else if(_mcvResult != ExaminedResultEnum.NoExamined && !mcv) {
      _errorMsg += mcvCheck.errorMsg;
      _okUpload = _okUpload && mcvCheck.okUpload;
    }
    const clResultCheck = checkExaminedResultEnum(
      arg.總膽固醇判讀,
      '總膽固醇判讀'
    );
    if(clResultCheck.isNull) {
      clResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += clResultCheck.errorMsg;
      _okUpload = _okUpload && clResultCheck.okUpload;
    }
    const _clResult = clResultCheck.code;
    const clInput = arg.總膽固醇;
    const clValue = parseRangeInput(clInput);
    const { code: cl, ...clCheck } = CheckBloodNumberRange(clValue, 0, { min: 0, max: 2000 },'總膽固醇');
    if(Number.isNaN(cl)) {
      _errorMsg+= '【總膽固醇】格式有誤，';
    } else if(_clResult != ExaminedResultEnum.NoExamined && !cl) {
      _errorMsg += clCheck.errorMsg;
      _okUpload = _okUpload && clCheck.okUpload;
    }
    const crResultCheck = checkExaminedResultEnum(
      arg.肌酸酐判讀,
      '肌酸酐判讀'
    );
    if(crResultCheck.isNull) {
      crResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += crResultCheck.errorMsg;
      _okUpload = _okUpload && crResultCheck.okUpload;
    }
    const _crResult = crResultCheck.code;
    const crInput = arg.肌酸酐;
    const crValue = parseRangeInput(crInput);
    const { code: cr, ...crCheck } = CheckBloodNumberRange(
      crValue || (arg as any)['肌酸酐 '],
      0,
      { min: 0, max: 20 },
      '肌酸酐'
    );
    if(Number.isNaN(cr)) {
      _errorMsg+= '【肌酸酐】格式有誤，';
    } else if(_crResult != ExaminedResultEnum.NoExamined && !cr) {
      _errorMsg += crCheck.errorMsg;
      _okUpload = _okUpload && crCheck.okUpload;
    }
    const uaResultCheck = checkExaminedResultEnum(
      arg.尿酸判讀,
      '尿酸判讀'
    );
    if(uaResultCheck.isNull) {
      uaResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += uaResultCheck.errorMsg;
      _okUpload = _okUpload && uaResultCheck.okUpload;
    }
    const _uaResult = uaResultCheck.code;
    const uaInput = arg.尿酸;
    const uaValue = parseRangeInput(uaInput);
    const { code: ua, ...uaCheck } = CheckBloodNumberRange(uaValue, 0, { min: 0, max: 20 }, '尿酸');
    if(Number.isNaN(ua)) {
      _errorMsg+= '【尿酸】格式有誤，';
    } else if(_uaResult != ExaminedResultEnum.NoExamined && !ua) {
      _errorMsg += uaCheck.errorMsg;
      _okUpload = _okUpload && uaCheck.okUpload;
    }
    const SGOTResultCheck = checkExaminedResultEnum(
      arg.SGOT判讀,
      'SGOT判讀'
    );
    if(SGOTResultCheck.isNull) {
      SGOTResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += SGOTResultCheck.errorMsg;
      _okUpload = _okUpload && SGOTResultCheck.okUpload;
    }
    const _SGOTResult = SGOTResultCheck.code;
    const SGOTInput = arg.SGOT;
    const SGOTValue = parseRangeInput(SGOTInput);
    const SGOTCheck = CheckBloodNumberRange(SGOTValue, 0, { min: 0, max: 3000 },'SGOT');
    if(Number.isNaN(SGOTCheck.code)) {
      _errorMsg+= '【SGOT】格式有誤，';
    } else if(_SGOTResult != ExaminedResultEnum.NoExamined && !SGOTCheck.code) {
      _errorMsg += SGOTCheck.errorMsg;
      _okUpload = _okUpload && SGOTCheck.okUpload;
    }
    const _SGOT = SGOTCheck.code;
    
    const SGPTResultCheck = checkExaminedResultEnum(
      arg.SGPT判讀,
      'SGPT判讀'
    );
    if(SGPTResultCheck.isNull) {
      SGPTResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += SGPTResultCheck.errorMsg;
      _okUpload = _okUpload && SGPTResultCheck.okUpload;
    }
    const _SGPTResult = SGPTResultCheck.code;
    const SGPTInput = arg.SGPT;
    const SGPTValue = parseRangeInput(SGPTInput);
    const SGPTCheck = CheckBloodNumberRange(SGPTValue, 0, { min: 0, max: 3000 }, 'SGPT');
    if(Number.isNaN(SGPTCheck.code)) {
      _errorMsg+= '【SGPT】格式有誤，';
    } else if(_SGPTResult != ExaminedResultEnum.NoExamined && !SGPTCheck.code) {
      _errorMsg += SGPTCheck.errorMsg;
      _okUpload = _okUpload && SGPTCheck.okUpload;
    }
    const _SGPT = SGPTCheck.code;
    const hbsaResultCheck = checkExaminedResultEnum(
      arg.B型肝炎表面抗原判讀,
      'B型肝炎表面抗原判讀'
    );
    if(hbsaResultCheck.isNull) {
      hbsaResultCheck.code = ExaminedResultEnum.NoProblem;
    } else {
      _errorMsg += hbsaResultCheck.errorMsg;
      _okUpload = _okUpload && hbsaResultCheck.okUpload;
    }
    const _hbsaResult = hbsaResultCheck.code;
    const hbsaInput = arg.B型肝炎表面抗原;
    const hbsaValue = parseRangeInput(hbsaInput);
    const { code: hbsa, ...hbsaCheck } = CheckBloodNumberRange(
      hbsaValue,
      0,
      { min: 0, max: 999999.99994 },
      'B型肝炎表面抗原'
    );
    if(Number.isNaN(hbsa)) {
      _errorMsg+= '【B型肝炎表面抗原】格式有誤，';
    }
    const antihbResultCheck =
      checkExaminedResultEnum(arg.B型肝炎表面抗體判讀, 'B型肝炎表面抗體判讀');
      if(antihbResultCheck.isNull) {
        antihbResultCheck.code = ExaminedResultEnum.NoProblem;
      } else {
        _errorMsg += antihbResultCheck.errorMsg;
        _okUpload = _okUpload && antihbResultCheck.okUpload;
      }
    const _antihbResult = antihbResultCheck.code;

    const antiHBInput = arg.B型肝炎表面抗體;
    const antiHBValue = parseRangeInput(antiHBInput);
    const { code: antiHB, ...antihbCheck } = CheckBloodNumberRange(
      antiHBValue,
      0,
      { min: 0, max: 999999.99994 },
      'B型肝炎表面抗體'
    );
    if(Number.isNaN(antiHB)) {
      _errorMsg+= '【B型肝炎表面抗體】格式有誤，';
    }
    const tgResultCheck = checkExaminedResultEnum(
      arg.三酸甘油酯判讀,
      '三酸甘油酯判讀'
    );
    if(tgResultCheck.isNull) {
      // 若有追加項目 顯示0無異常
      if(bloodSettingsData.includes('1')) {
        tgResultCheck.code = ExaminedResultEnum.NoProblem;   
      } else {
        // 若無追加項目 顯示9未受檢
        tgResultCheck.code = ExaminedResultEnum.NoExamined;
      }
    } else if(!tgResultCheck.okUpload && !tgResultCheck.isNull){
      _errorMsg += tgResultCheck.errorMsg;
      _okUpload = _okUpload && tgResultCheck.okUpload;
    }
    const _tgResult = tgResultCheck.code;
    const tgInput = arg.三酸甘油酯;
    const tgValue = parseRangeInput(tgInput);
    const tgCheck = CheckBloodNumberRange(
      tgValue,
      0,
      { min: 0, max: 999999.99994 },
      '三酸甘油酯'
    );
    if(Number.isNaN(tgCheck.code)) {
      _errorMsg+= '【三酸甘油酯】格式有誤，';
      _okUpload = false;
    }
    // if(bloodSettingsData.includes('1')) { // 若有追加項目 欄位需輸入值
    //   if(_tgResult != ExaminedResultEnum.NoExamined && !tgCheck.code) {
    //     _errorMsg += tgCheck.errorMsg;
    //     _okUpload = _okUpload && tgCheck.okUpload;
    //   }
    // } else if(arg.三酸甘油酯) {
    //   errorSettingMsg.push("三酸甘油酯");
    //   _okUpload = false;
    // }
    const _tg = tgCheck.code;
    const hdlResultCheck = checkExaminedResultEnum(
      arg.高密度膽固醇判讀,
      '高密度膽固醇判讀'
    );
    if(hdlResultCheck.isNull) {
      // 若有追加項目 顯示0無異常
      if(bloodSettingsData.includes('2')) {
        hdlResultCheck.code = ExaminedResultEnum.NoProblem;   
      } else {
        // 若無追加項目 顯示9未受檢
        hdlResultCheck.code = ExaminedResultEnum.NoExamined;
      }
    } else if(!hdlResultCheck.okUpload && !hdlResultCheck.isNull){
      _errorMsg += hdlResultCheck.errorMsg;
      _okUpload = _okUpload && hdlResultCheck.okUpload;
    }
    const _hdlResult = hdlResultCheck.code;
    const hdlInput = arg.高密度膽固醇;
    const hdlValue = parseRangeInput(hdlInput);
    const hdlCheck = CheckBloodNumberRange(
      hdlValue,
      0,
      { min: 0, max: 999999.99994 },
      '高密度膽固醇'
    );
    if(Number.isNaN(hdlCheck.code)) {
      _errorMsg+= '【高密度膽固醇】格式有誤，';
      _okUpload = false;
    }
    // if(bloodSettingsData.includes('2')) { // 若有追加項目 欄位需輸入值
    //   if(_hdlResult != ExaminedResultEnum.NoExamined && !hdlCheck.code) {
    //     _errorMsg += hdlCheck.errorMsg;
    //     _okUpload = _okUpload && hdlCheck.okUpload;
    //   }
    // } else if(arg.高密度膽固醇) {
    //   errorSettingMsg.push("高密度膽固醇");
    //   _okUpload = false;
    // }
    const _hdl = hdlCheck.code;
    const ldlResultCheck = checkExaminedResultEnum(
      arg.低密度膽固醇判讀,
      '低密度膽固醇判讀'
    );
    if(ldlResultCheck.isNull) {
      // 若有追加項目 顯示0無異常
      if(bloodSettingsData.includes('3')) {
        ldlResultCheck.code = ExaminedResultEnum.NoProblem;   
      } else {
        // 若無追加項目 顯示9未受檢
        ldlResultCheck.code = ExaminedResultEnum.NoExamined;
      }
    } else if(!ldlResultCheck.okUpload && !ldlResultCheck.isNull){
      _errorMsg += ldlResultCheck.errorMsg;
      _okUpload = _okUpload && ldlResultCheck.okUpload;
    }
    const _ldlResult = ldlResultCheck.code;
    const ldlInput = arg.低密度膽固醇;
    const ldlValue = parseRangeInput(ldlInput);
    const ldlCheck = CheckBloodNumberRange(
      ldlValue,
      0,
      { min: 0, max: 999999.99994 },
      '低密度膽固醇'
    );
    if(Number.isNaN(ldlCheck.code)) {
      _errorMsg+= '【低密度膽固醇】格式有誤，';
      _okUpload = false;
    }
    // if(bloodSettingsData.includes('3')) { // 若有追加項目 欄位需輸入值
    //   if(_ldlResult != ExaminedResultEnum.NoExamined && !ldlCheck.code) {
    //     _errorMsg += ldlCheck.errorMsg;
    //     _okUpload = _okUpload && ldlCheck.okUpload;
    //   }
    // } else if(arg.低密度膽固醇) {
    //   errorSettingMsg.push("低密度膽固醇");
    //   _okUpload = false;
    // }
    const _ldl = ldlCheck.code;
    const pcResultCheck = checkExaminedResultEnum(
      arg.空腹血糖判讀,
      '空腹血糖判讀'
    );
    if(pcResultCheck.isNull) {
      // 若有追加項目 顯示0無異常
      if(bloodSettingsData.includes('5')) {
        pcResultCheck.code = ExaminedResultEnum.NoProblem;   
      } else {
        // 若無追加項目 顯示9未受檢
        pcResultCheck.code = ExaminedResultEnum.NoExamined;
      }
    } else if(!pcResultCheck.okUpload && !pcResultCheck.isNull){
      _errorMsg += pcResultCheck.errorMsg;
      _okUpload = _okUpload && pcResultCheck.okUpload;
    }
    const _pcResult = pcResultCheck.code;
    const pcInput = arg.空腹血糖;
    const pcValue = parseRangeInput(pcInput);
    const pcCheck = CheckBloodNumberRange(pcValue, 0, { min: 0, max: 999999.99994 }, '空腹血糖');
    if(Number.isNaN(pcCheck.code)) {
      _errorMsg+= '【空腹血糖】格式有誤，';
      _okUpload = false;
    }
    // if(bloodSettingsData.includes('5')) { // 若有追加項目 欄位需輸入值
    //   if(_pcResult != ExaminedResultEnum.NoExamined && !pcCheck.code) {
    //     _errorMsg += pcCheck.errorMsg;
    //     _okUpload = _okUpload && pcCheck.okUpload;
    //   }
    // } else if(arg.空腹血糖) {
    //   errorSettingMsg.push("空腹血糖");
    //   _okUpload = false;
    // }
    const _pc = pcCheck.code;
    const acResultCheck = checkExaminedResultEnum(
      arg.飯後血糖判讀,
      '飯後血糖判讀'
    );
    if(acResultCheck.isNull) {
      // 若有追加項目 顯示0無異常
      if(bloodSettingsData.includes('4')) {
        acResultCheck.code = ExaminedResultEnum.NoProblem;   
      } else {
        // 若無追加項目 顯示9未受檢
        acResultCheck.code = ExaminedResultEnum.NoExamined;
      }
    } else if(!acResultCheck.okUpload && !acResultCheck.isNull){
      _errorMsg += acResultCheck.errorMsg;
      _okUpload = _okUpload && acResultCheck.okUpload;
    }
    const _acResult = acResultCheck.code;
    const acInput = arg.飯後血糖;
    const acValue = parseRangeInput(acInput);
    const acCheck = CheckBloodNumberRange(acValue, 0, { min: 0, max: 999999.99994 },'飯後血糖');
    if(Number.isNaN(acCheck.code)) {
      _errorMsg+= '【飯後血糖】格式有誤，';
      _okUpload = false;
    }
    // if(bloodSettingsData.includes('4')) { // 若有追加項目 欄位需輸入值
    //   if(_acResult != ExaminedResultEnum.NoExamined && !acCheck.code) {
    //     _errorMsg += acCheck.errorMsg;
    //     _okUpload = _okUpload && acCheck.okUpload;
    //   }
    // } else if(arg.飯後血糖) {
    //   errorSettingMsg.push("飯後血糖");
    //   _okUpload = false;
    // }
    const _ac = acCheck.code;
    const hbeagResultCheck = checkExaminedResultEnum(
      arg.B型肝炎e抗原判讀,
      'B型肝炎e抗原判讀'
    );
    if(bloodSettingsData.includes('6') && hbeagResultCheck.isNull) {
      hbeagResultCheck.code = ExaminedResultEnum.NoProblem;
    }
    const _hbeagResult = hbeagResultCheck.code;
    const hbeagInput = arg.B型肝炎e抗原;
    const hbeagValue = parseRangeInput(hbeagInput);
    const hbeagCheck = CheckBloodNumberRange(
      hbeagValue,
      0,
      { min: 0, max: 999999.99994 },
      'B型肝炎e抗原'
    );
    if(Number.isNaN(hbeagCheck.code)) {
      _errorMsg+= '【B型肝炎e抗原】格式有誤，';
      _okUpload = false;
    }
    // if(bloodSettingsData.includes('6')) {
    //   if(_hbeagResult != ExaminedResultEnum.NoExamined && !hbeagCheck.code) {
    //     _errorMsg += hbeagCheck.errorMsg;
    //     _okUpload = _okUpload && hbeagCheck.okUpload;
    //   }
    // } else if(arg.B型肝炎e抗原) {
    //   errorSettingMsg.push("B型肝炎e抗原");
    //   _okUpload = false;
    // }
    const _hbeag = hbeagCheck.code;
    const bunResultCheck = checkExaminedResultEnum(
      arg.血尿素氮判讀,
      '血尿素氮判讀'
    );
    if(bunResultCheck.isNull) {
      // 若有追加項目 顯示0無異常
      if(bloodSettingsData.includes('7')) {
        bunResultCheck.code = ExaminedResultEnum.NoProblem;   
      } else {
        // 若無追加項目 顯示9未受檢
        bunResultCheck.code = ExaminedResultEnum.NoExamined;
      }
    } else if(!bunResultCheck.okUpload && !bunResultCheck.isNull){
      _errorMsg += bunResultCheck.errorMsg;
      _okUpload = _okUpload && bunResultCheck.okUpload;
    }
    const _bunResult = bunResultCheck.code;
    const bunInput = arg.血尿素氮;
    const bunValue = parseRangeInput(bunInput);
    const bunCheck = CheckBloodNumberRange(bunValue, 0, { min: 0, max: 999999.99994 }, '血尿素氮');
    if(Number.isNaN(bunCheck.code)) {
      _errorMsg+= '【血尿素氮】格式有誤，';
      _okUpload = false;
    }
    // if(bloodSettingsData.includes('7')) {
    //   if(_bunResult != ExaminedResultEnum.NoExamined && !bunCheck.code) {
    //     _errorMsg += bunCheck.errorMsg;
    //     _okUpload = _okUpload && bunCheck.okUpload;
    //   }
    // } else if(arg.血尿素氮) {
    //   errorSettingMsg.push("血尿素氮");
    //   _okUpload = false;
    // }
    const _bun = bunCheck.code;
    const htResultCheck = checkExaminedResultEnum(
      arg.血球容積比判讀,
      '血球容積比判讀'
    );
    if(htResultCheck.isNull) {
      // 若有追加項目 顯示0無異常
      if(bloodSettingsData.includes('8')) {
        htResultCheck.code = ExaminedResultEnum.NoProblem;   
      } else {
        // 若無追加項目 顯示9未受檢
        htResultCheck.code = ExaminedResultEnum.NoExamined;
      }
    } else if(!htResultCheck.okUpload && !htResultCheck.isNull){
      _errorMsg += htResultCheck.errorMsg;
      _okUpload = _okUpload && htResultCheck.okUpload;
    }
    const _htResult = htResultCheck.code;
    const htInput = arg.血球容積比;
    const htValue = parseRangeInput(htInput);
    const { code: ht, ...htCheck } = CheckBloodNumberRange(
      htValue,
      0,
      { min: 0, max: 999999.99994 },
      '血球容積比'
    );
    if(Number.isNaN(ht)) {
      _errorMsg+= '【血球容積比】格式有誤，';
      _okUpload = false;
    }
    // if(bloodSettingsData.includes('8')) {
    //   if(_htResult != ExaminedResultEnum.NoExamined && !ht) {
    //     _errorMsg += htCheck.errorMsg;
    //     _okUpload = _okUpload && htCheck.okUpload;
    //   }
    // } else if(arg.血球容積比) {
    //   errorSettingMsg.push("血球容積比");
    //   _okUpload = false;
    // }
    //移除最後一個'，'
    if (_errorMsg.length != 0) {
      _errorMsg = _errorMsg.slice(0, -1);
    }
    // 追加設定提示訊息
    if(errorSettingMsg.length) {
      _errorMsg += "，尚未追加" + errorSettingMsg + "設定";
    }

    // console.log(arg);
    return {
      pid: _pid,
      sid: _sid,
      grade: _grade,
      no: _no,
      seat: _seat,
      sem: 0,
      year: 0,
      hb,
      wbc,
      rbc,
      pl,
      mcv,
      ht,
      cl,
      cr,
      ua,
      bun: _bun,
      got: _SGOT,
      gpt: _SGPT,
      hbsa,
      antiHB,
      hbResult: _hbResult,
      wbcResult: _wbcResult,
      rbcResult: _rbcResult,
      plResult: _plResult,
      mcvResult: _mcvResult,
      htResult: _htResult,
      clResult: _clResult,
      crResult: _crResult,
      uaResult: _uaResult,
      bunResult: _bunResult,
      gotResult: _SGOTResult,
      gptResult: _SGPTResult,
      hbsaResult: _hbsaResult,
      antiHBResult: _antihbResult,
      bloodCheckMemo: arg.備註,
      tgResult: _tgResult,
      tg: _tg,
      hdlResult: _hdlResult,
      hdl: _hdl,
      ldlResult: _ldlResult,
      ldl: _ldl,
      acResult: _acResult,
      ac: _ac,
      pcResult: _pcResult,
      pc: _pc,
      hBeAgResult: _hbeagResult,
      hBeAg: _hbeag,
      okUpload: _okUpload,
      remark: _errorMsg,
      nouse: arg.血色素判讀 === 'Hb' || arg.血色素判讀 === 'g/dl',
    };
  });

function normalizeKeys(obj: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k.replace(/[\s\u00A0\u3000\uFEFF]+/g, ''), // 清掉各種隱藏空白
      v
    ])
  );
}

function parseRangeInput(value: string) {
  if (typeof value === 'string') {
    value = value.trim(); // 去除前後空白
    // 處理 >
    if (value.startsWith('>')) {
      const numStr = value.substring(1).trim(); // 取出符號後的部分
      const num = Number(numStr);
      if (!Number.isNaN(num)) return num + 0.00001;
    }
    // 處理 <
    if (value.startsWith('<')) {
      const numStr = value.substring(1).trim();
      const num = Number(numStr);
      if (!Number.isNaN(num)) return num - 0.00001;
    }
  }

  // 一般數值或可轉換字串
  const normalNum = Number(value);
  return Number.isNaN(normalNum) ? NaN : normalNum;
}

export type PhiBlood = z.infer<typeof PhiBloodZod>;
export type PhiBloodFile = z.infer<typeof PhiBloodFileZod>;
